import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { usePresentationMode } from '../context/PresentationModeContext';
import { Button } from '../components/ui/button';
import PresentationModeButton from '../components/PresentationModeButton';
import { 
  ChevronRight, 
  ChevronDown,
  BarChart3,
  Activity,
  Target,
  TrendingUp,
  TrendingDown,
  Pickaxe,
  Factory,
  Layers,
  Gauge,
  FileText,
  Calendar,
  Globe,
  ArrowLeft,
  Gem,
  DollarSign,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';

// Import operation pages
import OperationsExecutiveSummary from './operations/OperationsExecutiveSummary';
import ProductionRevenue from './operations/ProductionRevenue';
import MineAccess from './operations/MineAccess';

// Import OperationsFlowMap component
import OperationsFlowMap from '../components/OperationsFlowMap';
import PlantRecovery from './operations/PlantRecovery';
import OreStocks from './operations/OreStocks';
import OperationalEfficiency from './operations/OperationalEfficiency';
import RiskDelivery from './operations/RiskDelivery';
import OperationsReports from './operations/OperationsReports';
import OperationsMultiPeriodComparative from './operations/OperationsMultiPeriodComparative';

// McKinsey-style Sidebar Navigation (following Finance pattern)
const SidebarNav = ({ activeSection, setActiveSection, language }) => {
  const [isExecution2026Expanded, setIsExecution2026Expanded] = useState(false);
  const [isReview2025Expanded, setIsReview2025Expanded] = useState(false);
  const [isReportsExpanded, setIsReportsExpanded] = useState(false);

  const mainSections = [
    { id: 'execution-2026', label: { en: 'Operational Execution 2026', pt: 'Execução Operacional 2026' }, icon: Target, hasSubsections: true },
    { id: 'review-2025', label: { en: 'Operational Review 2025', pt: 'Revisão Operacional 2025' }, icon: Calendar, hasSubsections: true },
    { id: 'comparative', label: { en: 'Multi-Period Comparison', pt: 'Comparativo Multi-Período' }, icon: BarChart3 },
    { id: 'reports', label: { en: 'Reports', pt: 'Relatórios' }, icon: FileText, hasReportsSubsections: true },
  ];

  // Subsections for Execution 2026
  const execution2026Subsections = [
    { id: 'mina-acesso-2026', label: { en: 'Mine & Ore Access', pt: 'Mina e Acesso ao Minério' }, icon: Pickaxe },
    { id: 'planta-recuperacao-2026', label: { en: 'Plant & Recovery', pt: 'Planta e Recuperação' }, icon: Factory },
    { id: 'stocks-minerio-2026', label: { en: 'Ore Stocks', pt: 'Stocks de Minério' }, icon: Layers },
    { id: 'producao-receita-2026', label: { en: 'Production & Revenue', pt: 'Produção e Receita' }, icon: TrendingUp },
    { id: 'eficiencia-operacional-2026', label: { en: 'Operational Efficiency', pt: 'Eficiência Operacional' }, icon: Gauge },
    { id: 'risco-entrega-2026', label: { en: 'Risk & Plan Delivery', pt: 'Risco e Entrega do Plano' }, icon: AlertTriangle },
  ];

  // Subsections for Review 2025
  const review2025Subsections = [
    { id: 'mina-acesso-2025', label: { en: 'Mine & Ore Access', pt: 'Mina e Acesso ao Minério' }, icon: Pickaxe },
    { id: 'planta-recuperacao-2025', label: { en: 'Plant & Recovery', pt: 'Planta e Recuperação' }, icon: Factory },
    { id: 'stocks-minerio-2025', label: { en: 'Ore Stocks', pt: 'Stocks de Minério' }, icon: Layers },
    { id: 'producao-receita-2025', label: { en: 'Production & Revenue', pt: 'Produção e Receita' }, icon: TrendingUp },
    { id: 'eficiencia-operacional-2025', label: { en: 'Operational Efficiency', pt: 'Eficiência Operacional' }, icon: Gauge },
    { id: 'risco-operacional-2025', label: { en: 'Operational Risk', pt: 'Risco Operacional' }, icon: AlertTriangle },
  ];

  const reportsSubsections = [
    { id: 'relatorios-mensais', label: { en: 'Monthly Production Report', pt: 'Relatório Mensal de Produção' }, icon: Calendar },
    { id: 'relatorios-plataforma', label: { en: 'Platform Generated Reports', pt: 'Relatórios Gerados pela Plataforma' }, icon: FileText },
  ];

  const isExecution2026Section = (id) => {
    return id === 'execution-2026' || id === 'overview-2026' || execution2026Subsections.some(sub => sub.id === id);
  };

  const isReview2025Section = (id) => {
    return id === 'review-2025' || id === 'overview-2025' || review2025Subsections.some(sub => sub.id === id);
  };

  const isReportsSection = (id) => {
    return id === 'reports' || reportsSubsections.some(sub => sub.id === id);
  };

  // Auto-expand only when navigating to a specific subsection (not overview)
  useEffect(() => {
    if (execution2026Subsections.some(sub => sub.id === activeSection)) {
      setIsExecution2026Expanded(true);
    }
    if (review2025Subsections.some(sub => sub.id === activeSection)) {
      setIsReview2025Expanded(true);
    }
    if (reportsSubsections.some(sub => sub.id === activeSection)) {
      setIsReportsExpanded(true);
    }
  }, [activeSection]);

  return (
    <div className="w-72 bg-gradient-to-br from-[#0A192F] via-[#112240] to-[#0A192F] flex flex-col h-screen sticky top-0 shadow-2xl">
      {/* Logo/Title */}
      <div className="p-6 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gold-400 to-gold-600 flex items-center justify-center shadow-lg">
            <Pickaxe className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-white font-serif text-lg font-bold tracking-wide">
              {language === 'pt' ? 'Operações' : 'Operations'}
            </h1>
            <p className="text-white/50 text-xs">{language === 'pt' ? 'Empresa ABC' : 'Company ABC'}</p>
          </div>
        </div>
      </div>

      {/* Navigation Items */}
      <div className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
        {mainSections.map((section) => {
          const Icon = section.icon;
          const isActive = section.hasSubsections 
            ? (section.id === 'execution-2026' ? isExecution2026Section(activeSection) : isReview2025Section(activeSection))
            : section.hasReportsSubsections
              ? isReportsSection(activeSection)
              : activeSection === section.id;
          
          const isExpanded = section.id === 'execution-2026' ? isExecution2026Expanded 
            : section.id === 'review-2025' ? isReview2025Expanded 
            : section.id === 'reports' ? isReportsExpanded 
            : false;

          return (
            <React.Fragment key={section.id}>
              <div
                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all duration-200 ${
                  isActive 
                    ? 'bg-white/10 text-white shadow-lg' 
                    : 'text-white/70 hover:bg-white/5 hover:text-white'
                }`}
                data-testid={`nav-${section.id}`}
              >
                <button
                  onClick={() => {
                    if (section.hasSubsections) {
                      if (section.id === 'execution-2026') {
                        setActiveSection('overview-2026');
                      } else if (section.id === 'review-2025') {
                        setActiveSection('overview-2025');
                      }
                    } else if (section.hasReportsSubsections) {
                      setActiveSection('relatorios-mensais');
                    } else {
                      setActiveSection(section.id);
                    }
                  }}
                  className="flex items-center gap-3 flex-1"
                >
                  <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-gold-400' : ''}`} />
                  <span className="text-sm font-medium text-left">
                    {section.label[language]}
                  </span>
                </button>
                {(section.hasSubsections || section.hasReportsSubsections) && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (section.id === 'execution-2026') {
                        setIsExecution2026Expanded(!isExecution2026Expanded);
                      } else if (section.id === 'review-2025') {
                        setIsReview2025Expanded(!isReview2025Expanded);
                      } else if (section.id === 'reports') {
                        setIsReportsExpanded(!isReportsExpanded);
                      }
                    }}
                    className="p-1 hover:bg-white/10 rounded transition-colors"
                    data-testid={`toggle-${section.id}`}
                  >
                    <ChevronRight className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''} ${isActive ? 'text-gold-400' : ''}`} />
                  </button>
                )}
              </div>

              {/* Execution 2026 Subsections */}
              {section.id === 'execution-2026' && isExecution2026Expanded && (
                <div className="ml-4 space-y-1 overflow-hidden transition-all duration-200">
                  {execution2026Subsections.map((sub) => {
                    const SubIcon = sub.icon;
                    const isSubActive = activeSection === sub.id;
                    
                    return (
                      <button
                        key={sub.id}
                        onClick={() => setActiveSection(sub.id)}
                        className={`w-[calc(100%-0.5rem)] flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-200 ${
                          isSubActive 
                            ? 'bg-white/10 text-white' 
                            : 'text-white/60 hover:bg-white/5 hover:text-white'
                        }`}
                        data-testid={`nav-${sub.id}`}
                      >
                        <SubIcon className={`w-4 h-4 flex-shrink-0 ${isSubActive ? 'text-gold-400' : ''}`} />
                        <span className="text-xs font-medium text-left">
                          {sub.label[language]}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Review 2025 Subsections */}
              {section.id === 'review-2025' && isReview2025Expanded && (
                <div className="ml-4 space-y-1 overflow-hidden transition-all duration-200">
                  {review2025Subsections.map((sub) => {
                    const SubIcon = sub.icon;
                    const isSubActive = activeSection === sub.id;
                    
                    return (
                      <button
                        key={sub.id}
                        onClick={() => setActiveSection(sub.id)}
                        className={`w-[calc(100%-0.5rem)] flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-200 ${
                          isSubActive 
                            ? 'bg-white/10 text-white' 
                            : 'text-white/60 hover:bg-white/5 hover:text-white'
                        }`}
                        data-testid={`nav-${sub.id}`}
                      >
                        <SubIcon className={`w-4 h-4 flex-shrink-0 ${isSubActive ? 'text-gold-400' : ''}`} />
                        <span className="text-xs font-medium text-left">
                          {sub.label[language]}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Reports Subsections */}
              {section.hasReportsSubsections && isReportsExpanded && (
                <div className="ml-4 space-y-1 overflow-hidden transition-all duration-200">
                  {reportsSubsections.map((sub) => {
                    const SubIcon = sub.icon;
                    const isSubActive = activeSection === sub.id;
                    
                    return (
                      <button
                        key={sub.id}
                        onClick={() => setActiveSection(sub.id)}
                        className={`w-[calc(100%-0.5rem)] flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-200 ${
                          isSubActive 
                            ? 'bg-white/10 text-white' 
                            : 'text-white/60 hover:bg-white/5 hover:text-white'
                        }`}
                        data-testid={`nav-${sub.id}`}
                      >
                        <SubIcon className={`w-4 h-4 flex-shrink-0 ${isSubActive ? 'text-gold-400' : ''}`} />
                        <span className="text-xs font-medium text-left">
                          {sub.label[language]}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

// Metric Card Component (following Finance pattern)
const MetricCard = ({ title, value, unit, change, changeUnit = '%', isPositive, icon: Icon, color = 'primary', onClick, clickable = false, subtitle, plan, variance }) => {
  return (
    <div 
      className={`bg-white rounded-xl border border-slate-200 p-5 hover:shadow-lg transition-all duration-300 ${clickable ? 'cursor-pointer hover:border-gold-300' : ''}`}
      onClick={onClick}
      data-testid={`metric-card-${title.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${
          color === 'gold' ? 'bg-gold-50' : color === 'emerald' ? 'bg-emerald-50' : 'bg-slate-100'
        }`}>
          <Icon className={`w-5 h-5 ${
            color === 'gold' ? 'text-gold-600' : color === 'emerald' ? 'text-emerald-600' : 'text-primary'
          }`} />
        </div>
        <div className="flex items-center gap-2">
          {change !== undefined && (
            <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
              isPositive ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'
            }`}>
              {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {parseFloat(change) > 0 ? '+' : ''}{change}{changeUnit}
            </div>
          )}
          {clickable && <ChevronRight className="w-4 h-4 text-slate-400" />}
        </div>
      </div>
      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">{title}</p>
      <p className="font-serif text-3xl font-semibold text-primary">
        {typeof value === 'number' ? value.toLocaleString() : value}
        <span className="text-base text-slate-400 ml-1 font-sans">{unit}</span>
      </p>
      {plan && (
        <p className="text-xs text-slate-400 mt-1">
          Plano: {typeof plan === 'number' ? plan.toLocaleString() : plan} {unit}
        </p>
      )}
      {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
    </div>
  );
};

// Highlight Card Component (following Finance pattern)
const HighlightCard = ({ icon: Icon, value, label, description, isPositive, iconBg }) => (
  <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
    <div className="flex items-center gap-3 mb-4">
      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${iconBg}`}>
        <Icon className="w-5 h-5" />
      </div>
      <span className={`font-mono text-2xl font-bold ${isPositive ? 'text-emerald-600' : 'text-red-500'}`}>
        {value}
      </span>
    </div>
    <h4 className="font-semibold text-primary mb-2">{label}</h4>
    <p className="text-sm text-slate-500 leading-relaxed">{description}</p>
  </div>
);

const OperationsPage = ({ year: propYear }) => {
  const { language, toggleLanguage } = useLanguage();
  const { isPresentationMode, registerNavigationCallbacks, unregisterNavigationCallbacks } = usePresentationMode();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeSection, setActiveSectionState] = useState('overview-2026');

  // Wrapper function that scrolls to top when changing section
  const setActiveSection = (newSection) => {
    setActiveSectionState(newSection);
    // Scroll to top when changing section
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  };

  // List of navigable sections in order for presentation mode
  const navigableSections = [
    'producao-receita-2026',
    'mina-acesso-2026',
    'planta-recuperacao-2026',
    'stocks-minerio-2026',
    'eficiencia-operacional-2026',
    'risco-entrega-2026',
    'producao-receita-2025',
    'mina-acesso-2025',
    'planta-recuperacao-2025',
    'stocks-minerio-2025',
    'eficiencia-operacional-2025',
    'risco-operacional-2025',
  ];

  // Register navigation callbacks for presentation mode
  useEffect(() => {
    const goToNextSection = () => {
      const currentIndex = navigableSections.indexOf(activeSection);
      if (currentIndex >= 0 && currentIndex < navigableSections.length - 1) {
        setActiveSection(navigableSections[currentIndex + 1]);
      } else if (currentIndex === -1) {
        // If current section not in list, go to first
        setActiveSection(navigableSections[0]);
      }
    };

    const goToPreviousSection = () => {
      const currentIndex = navigableSections.indexOf(activeSection);
      if (currentIndex > 0) {
        setActiveSection(navigableSections[currentIndex - 1]);
      } else if (currentIndex === -1) {
        // If current section not in list, go to last
        setActiveSection(navigableSections[navigableSections.length - 1]);
      }
    };

    if (isPresentationMode) {
      registerNavigationCallbacks(goToNextSection, goToPreviousSection);
    }

    return () => {
      unregisterNavigationCallbacks();
    };
  }, [isPresentationMode, activeSection, registerNavigationCallbacks, unregisterNavigationCallbacks]);

  // Initialize from URL params
  useEffect(() => {
    const sectionFromUrl = searchParams.get('section');
    if (sectionFromUrl) {
      setActiveSection(sectionFromUrl);
    }
  }, []);

  // Update URL when section changes
  useEffect(() => {
    setSearchParams({ section: activeSection });
  }, [activeSection, setSearchParams]);
  
  // Determine year from section
  const getYearFromSection = (sec) => {
    if (sec.includes('2026')) return 2026;
    if (sec.includes('2025')) return 2025;
    return propYear || 2026;
  };
  
  const year = getYearFromSection(activeSection);

  // Helper function to get the current section title for breadcrumb
  const getSectionTitle = () => {
    const sectionLabels = {
      'execution-2026': { en: 'Operational Execution 2026', pt: 'Execução Operacional 2026' },
      'review-2025': { en: 'Operational Review 2025', pt: 'Revisão Operacional 2025' },
      'overview-2026': { en: 'Operational Execution 2026', pt: 'Execução Operacional 2026' },
      'overview-2025': { en: 'Operational Review 2025', pt: 'Revisão Operacional 2025' },
      'reports': { en: 'Reports', pt: 'Relatórios' },
      'comparative': { en: 'Multi-Period Comparison', pt: 'Comparativo Multi-Período' },
      'producao-receita-2026': { en: 'Production & Revenue', pt: 'Produção e Receita' },
      'mina-acesso-2026': { en: 'Mine & Ore Access', pt: 'Mina e Acesso ao Minério' },
      'planta-recuperacao-2026': { en: 'Plant & Recovery', pt: 'Planta e Recuperação' },
      'stocks-minerio-2026': { en: 'Ore Stocks', pt: 'Stocks de Minério' },
      'eficiencia-operacional-2026': { en: 'Operational Efficiency', pt: 'Eficiência Operacional' },
      'risco-entrega-2026': { en: 'Risk & Plan Delivery', pt: 'Risco e Entrega do Plano' },
      'producao-receita-2025': { en: 'Production & Revenue', pt: 'Produção e Receita' },
      'mina-acesso-2025': { en: 'Mine & Ore Access', pt: 'Mina e Acesso ao Minério' },
      'planta-recuperacao-2025': { en: 'Plant & Recovery', pt: 'Planta e Recuperação' },
      'stocks-minerio-2025': { en: 'Ore Stocks', pt: 'Stocks de Minério' },
      'eficiencia-operacional-2025': { en: 'Operational Efficiency', pt: 'Eficiência Operacional' },
      'risco-operacional-2025': { en: 'Operational Risk', pt: 'Risco Operacional' },
      'relatorios-mensais': { en: 'Monthly Production Report', pt: 'Relatório Mensal de Produção' },
      'relatorios-plataforma': { en: 'Platform Generated Reports', pt: 'Relatórios Gerados pela Plataforma' },
    };
    return sectionLabels[activeSection]?.[language] || activeSection;
  };

  // Helper function to get the FlowMap component with proper props
  const getFlowMap = (yearOverride = null) => {
    const flowMapYear = yearOverride || (activeSection?.includes('2025') ? 2025 : 2026);
    return (
      <OperationsFlowMap
        activeSection={activeSection}
        setActiveSection={setActiveSection}
        year={flowMapYear}
        language={language}
      />
    );
  };

  // Render the appropriate component based on section
  const renderContent = () => {
    // Get flowMap component for pages that need it
    const flowMap = !activeSection?.includes('relatorios') && activeSection !== 'comparative' 
      ? getFlowMap() 
      : null;
    
    switch (activeSection) {
      // Overview pages
      case 'overview-2026':
        return <OperationsExecutiveSummary year={2026} setActiveSection={setActiveSection} flowMap={flowMap} isOverview={true} />;
      case 'overview-2025':
        return <OperationsExecutiveSummary year={2025} setActiveSection={setActiveSection} flowMap={flowMap} isOverview={true} />;
      
      // 2026 sections
      case 'producao-receita-2026':
        return <ProductionRevenue year={2026} setActiveSection={setActiveSection} flowMap={flowMap} />;
      case 'mina-acesso-2026':
        return <MineAccess year={2026} setActiveSection={setActiveSection} flowMap={flowMap} />;
      case 'planta-recuperacao-2026':
        return <PlantRecovery year={2026} setActiveSection={setActiveSection} flowMap={flowMap} />;
      case 'stocks-minerio-2026':
        return <OreStocks year={2026} setActiveSection={setActiveSection} flowMap={flowMap} />;
      case 'eficiencia-operacional-2026':
        return <OperationalEfficiency year={2026} setActiveSection={setActiveSection} flowMap={flowMap} />;
      case 'risco-entrega-2026':
        return <RiskDelivery year={2026} setActiveSection={setActiveSection} flowMap={flowMap} />;
      
      // 2025 sections
      case 'producao-receita-2025':
        return <ProductionRevenue year={2025} setActiveSection={setActiveSection} flowMap={flowMap} />;
      case 'mina-acesso-2025':
        return <MineAccess year={2025} setActiveSection={setActiveSection} flowMap={flowMap} />;
      case 'planta-recuperacao-2025':
        return <PlantRecovery year={2025} setActiveSection={setActiveSection} flowMap={flowMap} />;
      case 'stocks-minerio-2025':
        return <OreStocks year={2025} setActiveSection={setActiveSection} flowMap={flowMap} />;
      case 'eficiencia-operacional-2025':
        return <OperationalEfficiency year={2025} setActiveSection={setActiveSection} flowMap={flowMap} />;
      case 'risco-operacional-2025':
        return <RiskDelivery year={2025} setActiveSection={setActiveSection} flowMap={flowMap} />;
      
      // Reports
      case 'relatorios-mensais':
      case 'relatorios-plataforma':
        return <OperationsReports year={year} section={activeSection} />;
      
      // Comparative
      case 'comparative':
        return <OperationsMultiPeriodComparative year={year} language={language} flowMap={null} />;
      
      // Default - show overview 2026
      default:
        return <OperationsExecutiveSummary year={2026} setActiveSection={setActiveSection} flowMap={getFlowMap()} isOverview={true} />;
    }
  };

  return (
    <div className={`min-h-screen bg-slate-50 flex ${isPresentationMode ? 'presentation-mode' : ''}`} data-testid="operations-page">
      {/* McKinsey-style Sidebar - Hidden in Presentation Mode */}
      {!isPresentationMode && (
        <SidebarNav activeSection={activeSection} setActiveSection={setActiveSection} language={language} />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Header - Sticky (following Finance pattern exactly) */}
        <div className={`bg-white border-b border-slate-200 px-8 py-3 sticky top-0 z-40 shadow-sm ${isPresentationMode ? 'bg-primary text-white' : ''}`}>
          <div className="flex items-center justify-between">
            {/* Left side: Navigation + Department + Section */}
            <div className="flex items-center gap-3 min-w-0 flex-1">
              {!isPresentationMode && (
                <>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => navigate('/')}
                    className="text-slate-600 hover:text-primary flex-shrink-0"
                    data-testid="back-to-dashboard"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    <span className="font-serif text-sm">{language === 'pt' ? 'Cockpit Executivo' : 'Executive Cockpit'}</span>
                  </Button>
                  <div className="h-4 w-px bg-slate-300 flex-shrink-0"></div>
                </>
              )}
              <span className={`font-serif font-bold text-lg flex-shrink-0 ${isPresentationMode ? 'text-white' : 'text-primary'}`}>
                {isPresentationMode ? 'ABC' : (language === 'pt' ? 'Operações' : 'Operations')}
              </span>
              <ChevronRight className={`w-4 h-4 flex-shrink-0 ${isPresentationMode ? 'text-white/60' : 'text-slate-400'}`} />
              <span className={`font-serif text-base truncate ${isPresentationMode ? 'text-white/90' : 'text-slate-600'}`}>
                {getSectionTitle()}
              </span>
            </div>

            {/* Right side: Presentation Mode Button + Language Toggle */}
            <div className="flex items-center gap-4 flex-shrink-0">
              <PresentationModeButton />
              <button
                onClick={toggleLanguage}
                className={`flex items-center gap-2 px-3 py-1.5 rounded transition-colors ${
                  isPresentationMode 
                    ? 'bg-white/20 hover:bg-white/30 text-white' 
                    : 'bg-primary/10 hover:bg-primary/20 text-primary'
                }`}
                data-testid="operations-language-toggle"
              >
                <Globe className="w-4 h-4" />
                <span className="text-sm font-medium uppercase">{language}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Content Area - Lavender background like Finance */}
        <div className={`p-8 flex-1 bg-gradient-to-br from-slate-100 via-indigo-50/50 to-purple-50/30 overflow-auto ${isPresentationMode ? 'p-12' : ''}`}>
          <div 
            key={activeSection} 
            className={isPresentationMode ? 'presentation-fade-in' : ''}
          >
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OperationsPage;
